// src/components/StaffManagementView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import apiService from '../services/api';
import { RiAddLine, RiCheckLine, RiCloseLine, RiEditLine, RiUserLine } from 'react-icons/ri';

// Create Staff Modal
const CreateStaffModal = ({ isOpen, onClose, onSuccess, apiService: api, existingEmails = [] }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [role, setRole] = useState('');
  const [seniority, setSeniority] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const normalizedExistingEmails = new Set(
    existingEmails.map(e => String(e).toLowerCase())
  );
  const isDuplicateEmail =
    email.trim() && normalizedExistingEmails.has(email.trim().toLowerCase());

  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setDepartment('');
      setRole('');
      setSeniority('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await api.createStaff({
        name: name.trim(),
        emails: email.trim() ? [email.trim()] : [],
        department: department.trim() || undefined,
        role: role.trim() || undefined,
        seniority: seniority.trim() || undefined,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating staff:', err);
      setError(err.message || 'Failed to create staff member');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="rounded-xl p-6 w-full max-w-md"
        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-lg)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--pallium-purple-0-5)' }}
          >
            <RiUserLine className="w-5 h-5" style={{ color: 'var(--pallium-primary-purple)' }} />
          </div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--pallium-text-primary)' }}>
            Add New Staff Member
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
              Name <span style={{ color: 'var(--pallium-error-red)' }}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter full name..."
              className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
              style={{ border: '1px solid var(--pallium-stroke-light)' }}
              required
              autoFocus
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
              style={{ border: '1px solid var(--pallium-stroke-light)' }}
            />
            {isDuplicateEmail && (
              <div className="mt-2 text-xs" style={{ color: 'var(--pallium-warning-yellow)' }}>
                A staff member with this email already exists.
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
              Department
            </label>
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="e.g., Engineering, Sales..."
              className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
              style={{ border: '1px solid var(--pallium-stroke-light)' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                Role
              </label>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g., Manager..."
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ border: '1px solid var(--pallium-stroke-light)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                Seniority
              </label>
              <input
                type="text"
                value={seniority}
                onChange={(e) => setSeniority(e.target.value)}
                placeholder="e.g., Senior..."
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{ border: '1px solid var(--pallium-stroke-light)' }}
              />
            </div>
          </div>

          {error && (
            <div 
              className="mb-4 p-3 rounded-lg text-sm"
              style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ color: 'var(--pallium-text-secondary)', backgroundColor: 'var(--pallium-bg-secondary)' }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium rounded-lg text-white"
              style={{ backgroundColor: isLoading ? 'var(--pallium-icon-gray)' : 'var(--pallium-primary-purple)' }}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Add Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StaffManagementView = () => {
  // Try to get apiService from context, but have a fallback
  let contextApiService;
  try {
    const context = useOutletContext();
    contextApiService = context?.apiService;
  } catch (e) {
    console.warn("useOutletContext not available for StaffManagementView, falling back to direct import.");
  }
  const activeApiService = contextApiService || apiService;

  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchStaffData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await activeApiService.getAllStaff(showInactive);
      setStaffData(result.map(record => ({
        id: record.id || record.staffId,
        name: record.name || record.staffName,
        emails: record.emails || [],
        department: record.department,
        role: record.role,
        seniority: record.seniority,
        imageUrl: record.imageUrl || record.staffImageUrl,
        active: record.active !== false, // Default to true if not specified
      })));
    } catch (err) {
      console.error("Error fetching staff list:", err);
      setError("Failed to load staff data. " + err.message);
    } finally {
      setLoading(false);
    }
  }, [activeApiService, showInactive]);

  useEffect(() => {
    fetchStaffData();
  }, [fetchStaffData]);

  const handleToggleActive = async (staffId, currentActive) => {
    try {
      await activeApiService.toggleStaffActive(staffId, !currentActive);
      setStaffData(prev => prev.map(s => 
        s.id === staffId ? { ...s, active: !currentActive } : s
      ));
    } catch (err) {
      console.error('Error toggling staff active status:', err);
    }
  };

  const handleStartEdit = (staff) => {
    setEditingId(staff.id);
    setEditForm({
      name: staff.name,
      department: staff.department || '',
      role: staff.role || '',
      seniority: staff.seniority || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (staffId) => {
    try {
      await activeApiService.updateStaff(staffId, editForm);
      setStaffData(prev => prev.map(s => 
        s.id === staffId ? { ...s, ...editForm } : s
      ));
      setEditingId(null);
      setEditForm({});
    } catch (err) {
      console.error('Error updating staff:', err);
    }
  };

  // Get unique departments
  const departments = [...new Set(staffData.map(s => s.department).filter(d => d))].sort();

  const emailCounts = staffData.reduce((acc, staff) => {
    (staff.emails || []).forEach(email => {
      const key = String(email).toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
    });
    return acc;
  }, {});

  const duplicateEmails = new Set(
    Object.keys(emailCounts).filter(email => emailCounts[email] > 1)
  );

  const hasDuplicateEmail = (staff) =>
    (staff.emails || []).some(email =>
      duplicateEmails.has(String(email).toLowerCase())
    );

  // Filter staff
  const filteredStaff = staffData.filter(staff => {
    if (selectedDepartment && staff.department !== selectedDepartment) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--pallium-primary-purple)' }}></div>
        <span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>Loading staff data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-md" style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}>
        <strong>Error:</strong> {error}
        <button 
          onClick={fetchStaffData} 
          className="ml-2 text-white font-bold py-1 px-2 rounded text-sm"
          style={{ backgroundColor: 'var(--pallium-error-red)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      {/* Header with Filters */}
      <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--pallium-text-primary)' }}>Staff Management</h2>
            <p className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
              {filteredStaff.length} staff member{filteredStaff.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-end">
            {/* Department Filter */}
            <div className="flex flex-col">
              <label className="text-xs font-semibold mb-1" style={{ color: 'var(--pallium-primary-purple)' }}>
                DEPARTMENT
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-3 py-2 rounded-md border text-sm"
                style={{ borderColor: 'var(--pallium-stroke-light)', color: 'var(--pallium-text-primary)' }}
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            {/* Show Inactive Toggle */}
            <label className="flex items-center gap-2 cursor-pointer px-3 py-2">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--pallium-primary-purple)' }}
              />
              <span className="text-sm font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                Show inactive
              </span>
            </label>

            {/* Add Staff Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white"
              style={{ backgroundColor: 'var(--pallium-primary-purple)' }}
            >
              <RiAddLine className="w-4 h-4" />
              Add Staff
            </button>
          </div>
        </div>
      </div>

      {/* Staff Table */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}>
        <table className="min-w-full">
          <thead>
            <tr style={{ backgroundColor: 'var(--pallium-purple-0-5)' }}>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase" style={{ color: 'var(--pallium-primary-purple)' }}>Name</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase" style={{ color: 'var(--pallium-primary-purple)' }}>Email</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase" style={{ color: 'var(--pallium-primary-purple)' }}>Department</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase" style={{ color: 'var(--pallium-primary-purple)' }}>Role</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase" style={{ color: 'var(--pallium-primary-purple)' }}>Status</th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase" style={{ color: 'var(--pallium-primary-purple)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center" style={{ color: 'var(--pallium-text-secondary)' }}>
                  No staff members found.
                </td>
              </tr>
            ) : (
              filteredStaff.map((staff) => (
                <tr key={staff.id} style={{ borderBottom: '1px solid var(--pallium-stroke-light)' }}>
                  <td className="px-6 py-4">
                    {editingId === staff.id ? (
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="px-2 py-1 text-sm rounded border"
                        style={{ borderColor: 'var(--pallium-stroke-light)' }}
                      />
                    ) : (
                      <div className="flex items-center">
                        <div 
                          className="w-8 h-8 rounded-full mr-3 flex items-center justify-center font-semibold text-sm"
                          style={{ backgroundColor: staff.active ? 'var(--pallium-purple-1)' : 'var(--pallium-stroke-lighter)', color: staff.active ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-secondary)' }}
                        >
                          {staff.name?.charAt(0) || '?'}
                        </div>
                        <span style={{ color: staff.active ? 'var(--pallium-text-primary)' : 'var(--pallium-text-secondary)' }}>
                          {staff.name || 'N/A'}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
                      {staff.emails?.[0] || 'N/A'}
                      {hasDuplicateEmail(staff) && (
                        <span className="ml-2 text-xs" style={{ color: 'var(--pallium-warning-yellow)' }}>
                          Duplicate email
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {editingId === staff.id ? (
                      <input
                        type="text"
                        value={editForm.department}
                        onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                        className="px-2 py-1 text-sm rounded border"
                        style={{ borderColor: 'var(--pallium-stroke-light)' }}
                      />
                    ) : (
                      <span className="text-sm" style={{ color: 'var(--pallium-text-primary)' }}>
                        {staff.department || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingId === staff.id ? (
                      <input
                        type="text"
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        className="px-2 py-1 text-sm rounded border"
                        style={{ borderColor: 'var(--pallium-stroke-light)' }}
                      />
                    ) : (
                      <span className="text-sm" style={{ color: 'var(--pallium-text-primary)' }}>
                        {staff.role || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleToggleActive(staff.id, staff.active)}
                      className="px-3 py-1 text-xs font-medium rounded-full transition-colors"
                      style={staff.active 
                        ? { backgroundColor: 'rgba(19, 185, 85, 0.15)', color: 'var(--pallium-success-green)' }
                        : { backgroundColor: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-text-secondary)' }
                      }
                    >
                      {staff.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {editingId === staff.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleSaveEdit(staff.id)}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ backgroundColor: 'rgba(19, 185, 85, 0.15)', color: 'var(--pallium-success-green)' }}
                          title="Save"
                        >
                          <RiCheckLine className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ backgroundColor: 'rgba(252, 57, 57, 0.15)', color: 'var(--pallium-error-red)' }}
                          title="Cancel"
                        >
                          <RiCloseLine className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleStartEdit(staff)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ backgroundColor: 'var(--pallium-purple-0-5)', color: 'var(--pallium-primary-purple)' }}
                        title="Edit"
                      >
                        <RiEditLine className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Staff Modal */}
      <CreateStaffModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchStaffData}
        apiService={activeApiService}
        existingEmails={staffData.flatMap(s => s.emails || [])}
      />
    </div>
  );
};

export default StaffManagementView;
